import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { Camera, Loader2, RotateCw, ZoomIn, ZoomOut, X, ArrowLeft } from "lucide-react";
import toast from "react-hot-toast";
import { urlOptimizada, TAMANIO_MINIATURA, comprimirImagenes } from "../../utils/imagenes";

export default function EditarServicio() {
    const { id } = useParams();
    const navigate = useNavigate();

    const [clientes, setClientes] = useState([]);
    const [loading, setLoading] = useState(false);
    const [fetching, setFetching] = useState(true);

    const [servicio, setServicio] = useState({
        cliente_id: "",
        mod_de_ordeniadora: "",
        ordenios_por_dia: "",
        fecha_servicio: "",
        fecha_cambio_pezonera: "",
        servicio_prog_cada: "",
        proximo_servicio: "",
        num_de_bajadas: "",
        vida_util_pezonera: "",
        vacas_en_ordenio: "",
        proximo_recambio_pezoneras: "",
        dias_utiles_pezonera: "",
        comentario: "",
        proximo_servicio_manual: false,
        proximo_recambio_manual: false,
    });

    const [imagenesExistentes, setImagenesExistentes] = useState([]);
    const [imagenesNuevas, setImagenesNuevas] = useState([]);
    // Cantidad de tandas de fotos que se están achicando (bloquea el guardado mientras tanto)
    const [procesandoFotos, setProcesandoFotos] = useState(0);

    // Achicamos las fotos nuevas antes de agregarlas (subida mucho más rápida desde el campo)
    const agregarImagenesNuevas = async (files) => {
        if (!files || files.length === 0) return;
        const lista = Array.from(files);
        setProcesandoFotos(n => n + 1);
        try {
            const comprimidas = await comprimirImagenes(lista);
            setImagenesNuevas(prev => [...prev, ...comprimidas]);
        } finally {
            setProcesandoFotos(n => n - 1);
        }
    };
    const [imagenesEliminar, setImagenesEliminar] = useState([]);

    const [imagenModal, setImagenModal] = useState(null);
    const [zoom, setZoom] = useState(1);
    const [rotacion, setRotacion] = useState(0);
    // URL de la foto del visor que ya terminó de cargar (para mostrar el "cargando...")
    const [imagenCargada, setImagenCargada] = useState("");

    useEffect(() => {
        const cargarDatos = async () => {
            await obtenerClientes();
            await obtenerServicio();
            setFetching(false);
        };
        cargarDatos();
    }, [id]);

    const obtenerClientes = async () => {
        try {
            const res = await axios.get(`${process.env.REACT_APP_BASE_URL}/cliente`);
            setClientes(res.data);
        } catch (err) {
            toast.error("❌ Error al cargar clientes");
        }
    };

    const obtenerServicio = async () => {
        try {
            const res = await axios.get(`${process.env.REACT_APP_BASE_URL}/servicio/${id}`);
            const s = res.data;
            setServicio({
                ...s,
                proximo_servicio_manual: s.proximo_servicio_manual === 1 || s.proximo_servicio_manual === true,
                proximo_recambio_manual: s.proximo_recambio_manual === 1 || s.proximo_recambio_manual === true,
            });
            // 🛠️ PARSEAR LAS IMÁGENES DE CLOUDINARY
            let imagenes = [];

            if (s.imagenes_remito) {
                // Si ya es un array (porque el backend lo parseó), lo usamos directo
                if (Array.isArray(s.imagenes_remito)) {
                    imagenes = s.imagenes_remito;
                } else {
                    // Si viene como string '["url1", "url2"]', lo convertimos en Array real
                    try {
                        imagenes = JSON.parse(s.imagenes_remito);
                    } catch (e) {
                        // Si falla el parse (ej: es una sola url string), lo metemos en un array
                        imagenes = [s.imagenes_remito];
                    }
                }
            }

            setImagenesExistentes(imagenes);

        } catch (err) {
            toast.error("❌ Error al cargar el servicio");
        }
    };

    // 📅 LOGICA DE CALCULOS (Respetando el video del cliente)
    useEffect(() => {
        if (fetching) return;

        const {
            fecha_servicio,
            fecha_cambio_pezonera,
            servicio_prog_cada,
            vida_util_pezonera,
            num_de_bajadas,
            vacas_en_ordenio,
            ordenios_por_dia,
            proximo_servicio_manual,
            proximo_recambio_manual,
        } = servicio;

        // 🔹 Próximo Servicio Técnico
        if (fecha_servicio && servicio_prog_cada && !proximo_servicio_manual) {
            const nuevaFecha = new Date(fecha_servicio);
            nuevaFecha.setMonth(nuevaFecha.getMonth() + Number(servicio_prog_cada));
            const iso = nuevaFecha.toISOString().split("T")[0];
            if (iso !== servicio.proximo_servicio) {
                setServicio(prev => ({ ...prev, proximo_servicio: iso }));
            }
        }

        // 🔹 Próximo Recambio Pezoneras (USA fecha_cambio_pezonera)
        if (fecha_cambio_pezonera && vida_util_pezonera && num_de_bajadas && vacas_en_ordenio && ordenios_por_dia && !proximo_recambio_manual) {
            const denominador = Number(vacas_en_ordenio) * Number(ordenios_por_dia);
            if (denominador > 0) {
                const diasUtiles = (Number(vida_util_pezonera) * Number(num_de_bajadas)) / denominador;
                const fechaRecambio = new Date(fecha_cambio_pezonera);
                fechaRecambio.setDate(fechaRecambio.getDate() + Math.round(diasUtiles));
                const isoRecambio = fechaRecambio.toISOString().split("T")[0];

                if (isoRecambio !== servicio.proximo_recambio_pezoneras) {
                    setServicio(prev => ({
                        ...prev,
                        dias_utiles_pezonera: Math.round(diasUtiles),
                        proximo_recambio_pezoneras: isoRecambio
                    }));
                }
            }
        }
    }, [servicio.fecha_servicio, servicio.fecha_cambio_pezonera, servicio.servicio_prog_cada, servicio.vida_util_pezonera, servicio.num_de_bajadas, servicio.vacas_en_ordenio, servicio.ordenios_por_dia, servicio.proximo_servicio_manual, servicio.proximo_recambio_manual, fetching]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setServicio(prev => {
            let n = { ...prev, [name]: value };

            if (name === "fecha_servicio") {
                n.fecha_cambio_pezonera = value; // Sincroniza al cambiar la principal
                n.proximo_servicio_manual = false;
                n.proximo_recambio_manual = false;
            }

            if (name === "proximo_servicio") n.proximo_servicio_manual = true;
            if (name === "proximo_recambio_pezoneras") n.proximo_recambio_manual = true;

            if (name === "servicio_prog_cada") n.proximo_servicio_manual = false;
            if (["vida_util_pezonera", "num_de_bajadas", "vacas_en_ordenio", "ordenios_por_dia", "fecha_cambio_pezonera"].includes(name)) {
                n.proximo_recambio_manual = false;
            }
            return n;
        });
    };

    // const handleSubmit = async (e) => {
    //     e.preventDefault();
    //     setLoading(true);
    //     try {
    //         const formData = new FormData();

    //         Object.entries(servicio).forEach(([key, value]) => {
    //             if (value !== null && value !== "") formData.append(key, value);
    //         });
    //         // 2. ⚠️ LAS IMÁGENES QUE QUEDARON (Clave para Cloudinary)
    //         // Mandamos el array de URLs que NO borraste
    //         formData.append("imagenes_existentes", JSON.stringify(imagenesExistentes));

    //         // 3. Imágenes nuevas que sacaste con la cámara
    //         imagenesNuevas.forEach(file => formData.append("imagenes_nuevas", file));

    //         // 4. Imágenes que el usuario marcó para borrar definitivamente
    //         if (imagenesEliminar.length > 0) {
    //             formData.append("imagenes_eliminar", JSON.stringify(imagenesEliminar));
    //         }

    //         await axios.put(`${process.env.REACT_APP_BASE_URL}/servicio/${id}`, formData, {
    //             headers: { "Content-Type": "multipart/form-data" }
    //         });
    //         toast.success("Servicio actualizado correctamente");
    //         setTimeout(() => navigate("/layout/ver-servicios"), 200);
    //     } catch (error) {
    //         console.error("Error al actualizar:", error);
    //         toast.error("Error al actualizar el servicio");
    //     } finally { setLoading(false); }
    // };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        // Bloqueamos el botón para evitar doble envío
        setLoading(true);

        try {
            const formData = new FormData();

            // 1. Campos de texto normales
            Object.entries(servicio).forEach(([key, value]) => {
                // Evitamos mandar campos vacíos o nulls innecesarios
                if (value !== null && value !== "") formData.append(key, value);
            });

            // 2. Imágenes que se mantienen (Cloudinary)
            formData.append("imagenes_existentes", JSON.stringify(imagenesExistentes));

            // 3. Imágenes nuevas de la cámara
            imagenesNuevas.forEach(file => formData.append("imagenes_nuevas", file));

            // 4. Imágenes a eliminar de la nube
            if (imagenesEliminar.length > 0) {
                formData.append("imagenes_eliminar", JSON.stringify(imagenesEliminar));
            }

            // 🚀 Definimos la promesa de la petición
            const promise = axios.put(`${process.env.REACT_APP_BASE_URL}/servicio/${id}`, formData, {
                headers: { "Content-Type": "multipart/form-data" }
            });

            // ⚡ Ejecutamos el toast profesional
            toast.promise(
                promise,
                {
                    loading: "Actualizando datos y gestionando imágenes en la nube...",
                    success: (res) => {
                        setLoading(false);
                        // Esperamos un poquito para que el técnico vea el éxito antes de navegar
                        setTimeout(() => navigate("/layout/ver-servicios"), 1000);
                        return "¡Servicio actualizado con éxito!";
                    },
                    error: (err) => {
                        setLoading(false);
                        const msg = err.response?.data?.message || "Error al actualizar el servicio";
                        return <b>{msg}</b>;
                    },
                },
                {
                    success: {
                        duration: 4000,
                    },
                    error: {
                        duration: 5000,
                    }
                }
            );

        } catch (error) {
            setLoading(false);
            console.error("Error al preparar el envío:", error);
            toast.error("Ocurrió un error inesperado");
        }
    };

    if (fetching) return <div className="flex justify-center mt-20"><Loader2 className="animate-spin text-blue-600" size={40} /></div>;

    // 🎨 Estilos de la interfaz (solo visual)
    const labelCls = "block text-[11px] font-bold text-slate-600 uppercase tracking-wide mb-1";
    const inputCls = "w-full h-10 border border-slate-300 rounded-lg px-3 text-[13px] text-slate-900 bg-white outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all";
    const readCls = "w-full h-10 border border-blue-100 rounded-lg px-3 text-[13px] font-bold text-blue-900 bg-blue-50/60 outline-none cursor-default";
    const manualCls = (manual) =>
        `w-full h-10 border rounded-lg px-3 text-[13px] font-bold outline-none focus:ring-2 focus:ring-blue-500 transition-all ${manual ? "border-orange-300 bg-orange-50 text-orange-700" : "border-blue-100 bg-blue-50/60 text-blue-900"}`;
    const tituloSeccion = "text-[11px] font-black text-blue-700 uppercase tracking-[0.15em] mb-3 flex items-center gap-2 after:flex-1 after:h-px after:bg-slate-100";
    const badgeManual = <span className="ml-1 px-1.5 py-px rounded bg-orange-100 text-orange-700 text-[9px] font-black normal-case tracking-normal">manual</span>;

    return (
        <div className="h-full flex flex-col">

            <form
                onSubmit={handleSubmit}
                className="flex-1 min-h-0 flex flex-col bg-white shadow-sm border border-slate-200 rounded-xl overflow-hidden"
            >
                <div className="flex-1 min-h-0 overflow-y-auto scroll-fino p-4 lg:p-5 space-y-5">

                    {/* ── CLIENTE Y FECHAS ── */}
                    <section>
                        <h4 className={tituloSeccion}>Cliente y fechas</h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
                            {/* 1. Cliente */}
                            <div className="md:col-span-2">
                                <label className={labelCls}>Cliente <span className="text-red-500">*</span></label>
                                <select
                                    name="cliente_id"
                                    value={servicio.cliente_id}
                                    onChange={(e) => {
                                        const cid = e.target.value;
                                        const c = clientes.find(item => String(item.id) === String(cid));
                                        setServicio(prev => ({ ...prev, cliente_id: cid, mod_de_ordeniadora: c?.descripcion_ordeniadora || "" }));
                                    }}
                                    className={inputCls}
                                    required
                                >
                                    <option value="">Seleccionar cliente...</option>
                                    {clientes.map((c) => (
                                        <option key={c.id} value={c.id}>
                                            {c.nombre}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            {/* 4. Modelo De Ordeñadora */}
                            <div className="md:col-span-2">
                                <label className={labelCls}>Modelo de ordeñadora</label>
                                <input
                                    type="text"
                                    name="mod_de_ordeniadora"
                                    value={servicio.mod_de_ordeniadora}
                                    readOnly
                                    title={servicio.mod_de_ordeniadora}
                                    className="w-full h-10 border border-slate-200 rounded-lg px-3 text-[13px] bg-slate-100 text-slate-600 cursor-not-allowed truncate"
                                />
                            </div>

                            {/* 2. Fecha de servicio técnico */}
                            <div>
                                <label className={labelCls}>Fecha último servicio técnico <span className="text-red-500">*</span></label>
                                <input
                                    type="date"
                                    name="fecha_servicio"
                                    value={servicio.fecha_servicio}
                                    onChange={handleChange}
                                    className={inputCls}
                                    required
                                />
                            </div>

                            {/* 3. Fecha último cambio de pezoneras */}
                            <div>
                                <label className={labelCls}>Fecha último cambio de pezoneras <span className="text-red-500">*</span></label>
                                <input
                                    type="date"
                                    name="fecha_cambio_pezonera"
                                    value={servicio.fecha_cambio_pezonera}
                                    onChange={handleChange}
                                    className={inputCls}
                                    required
                                />
                            </div>

                            {/* 8. Servicio programado Cada (Meses) */}
                            <div>
                                <label className={labelCls}>Servicio programado cada (meses)</label>
                                <input
                                    type="number"
                                    name="servicio_prog_cada"
                                    value={servicio.servicio_prog_cada}
                                    onChange={handleChange}
                                    className={inputCls}
                                />
                            </div>

                            {/* 5. Ordeños por día */}
                            <div>
                                <label className={labelCls}>Ordeños por día</label>
                                <input
                                    type="number"
                                    name="ordenios_por_dia"
                                    value={servicio.ordenios_por_dia}
                                    onChange={handleChange}
                                    className={inputCls}
                                />
                            </div>
                        </div>
                    </section>

                    {/* ── DATOS DEL TAMBO + CÁLCULOS ── */}
                    <div className="grid grid-cols-1 xl:grid-cols-2 gap-5">
                        <section>
                            <h4 className={tituloSeccion}>Datos del tambo</h4>
                            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                                {/* 9. Cantidad de puestos de ordeñe */}
                                <div>
                                    <label className={labelCls}>Puestos de ordeñe</label>
                                    <input
                                        type="number"
                                        name="num_de_bajadas"
                                        value={servicio.num_de_bajadas}
                                        onChange={handleChange}
                                        className={inputCls}
                                    />
                                </div>

                                {/* 10. Vida útil de pezonera */}
                                <div>
                                    <label className={labelCls}>Vida útil pezonera</label>
                                    <input
                                        type="number"
                                        name="vida_util_pezonera"
                                        value={servicio.vida_util_pezonera}
                                        onChange={handleChange}
                                        className={inputCls}
                                    />
                                </div>

                                {/* 11. Vacas en ordeño */}
                                <div>
                                    <label className={labelCls}>Vacas en ordeño</label>
                                    <input
                                        type="number"
                                        name="vacas_en_ordenio"
                                        value={servicio.vacas_en_ordenio}
                                        onChange={handleChange}
                                        className={inputCls}
                                    />
                                </div>
                            </div>
                        </section>

                        <section>
                            <h4 className={tituloSeccion}>Cálculos automáticos</h4>
                            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                                {/* 6. Próximo servicio técnico (editable: pasa a manual) */}
                                <div>
                                    <label className={labelCls}>Próximo servicio técnico {servicio.proximo_servicio_manual && badgeManual}</label>
                                    <input
                                        type="date"
                                        name="proximo_servicio"
                                        value={servicio.proximo_servicio}
                                        onChange={handleChange}
                                        className={manualCls(servicio.proximo_servicio_manual)}
                                    />
                                </div>

                                {/* 7. Próximo cambio de pezoneras (editable: pasa a manual) */}
                                <div>
                                    <label className={labelCls}>Próximo cambio pezoneras {servicio.proximo_recambio_manual && badgeManual}</label>
                                    <input
                                        type="date"
                                        name="proximo_recambio_pezoneras"
                                        value={servicio.proximo_recambio_pezoneras}
                                        onChange={handleChange}
                                        className={manualCls(servicio.proximo_recambio_manual)}
                                    />
                                </div>

                                {/* 12. Días útiles de la pezonera */}
                                <div>
                                    <label className={labelCls}>Días útiles pezonera</label>
                                    <input
                                        type="text"
                                        value={servicio.dias_utiles_pezonera}
                                        readOnly
                                        className={readCls}
                                    />
                                </div>
                            </div>
                        </section>
                    </div>

                    {/* ── COMENTARIO Y REMITO ── */}
                    <section>
                        <h4 className={tituloSeccion}>Comentario y remito</h4>
                        <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
                            {/* 13. Comentario */}
                            <div className="flex flex-col">
                                <label className={labelCls}>Comentario</label>
                                <textarea
                                    name="comentario"
                                    value={servicio.comentario}
                                    onChange={handleChange}
                                    rows={4}
                                    placeholder="Observaciones del servicio..."
                                    className="w-full flex-1 min-h-[110px] border border-slate-300 rounded-lg p-3 text-[13px] outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
                                />
                            </div>

                            {/* 📷 Galería de fotos (Existentes y Nuevas) + carga */}
                            <div className="flex flex-col">
                                <label className={labelCls}>
                                    Fotos del remito
                                    {procesandoFotos > 0 && (
                                        <span className="ml-2 inline-flex items-center gap-1 text-blue-600 normal-case tracking-normal">
                                            <Loader2 className="animate-spin" size={12} /> procesando...
                                        </span>
                                    )}
                                </label>
                                <div className="flex-1 min-h-[110px] border border-slate-200 rounded-lg p-2">
                                    <div className="grid grid-cols-4 sm:grid-cols-6 gap-2">
                                        {/* Galería de fotos (Existentes) */}
                                        {imagenesExistentes.map((img, i) => (
                                            <div key={`old-${i}`} className="relative border border-slate-200 rounded-md h-[72px] bg-slate-50">
                                                <img
                                                    /* Miniatura liviana; al tocarla se abre la versión optimizada */
                                                    src={urlOptimizada(img, TAMANIO_MINIATURA)}
                                                    alt="Remito"
                                                    loading="lazy"
                                                    className="object-cover w-full h-full rounded-md cursor-pointer"
                                                    onClick={() => setImagenModal(urlOptimizada(img))}
                                                />
                                                <button
                                                    type="button"
                                                    onClick={() => {
                                                        setImagenesEliminar(prev => [...prev, img]);
                                                        setImagenesExistentes(prev => prev.filter(item => item !== img));
                                                    }}
                                                    title="Quitar foto"
                                                    className="absolute -top-1.5 -right-1.5 bg-red-600 text-white rounded-full w-5 h-5 flex items-center justify-center shadow-md"
                                                >
                                                    <X size={12} />
                                                </button>
                                            </div>
                                        ))}
                                        {/* Fotos nuevas (todavía no subidas) */}
                                        {imagenesNuevas.map((file, i) => (
                                            <div key={`new-${i}`} className="relative border-2 border-blue-300 rounded-md h-[72px]">
                                                <img
                                                    src={URL.createObjectURL(file)}
                                                    alt="Nueva foto"
                                                    className="object-cover w-full h-full rounded"
                                                />
                                                <span className="absolute bottom-0 inset-x-0 bg-blue-600/90 text-white text-[8px] font-black uppercase text-center">Nueva</span>
                                                <button
                                                    type="button"
                                                    onClick={() => setImagenesNuevas(prev => prev.filter((_, idx) => idx !== i))}
                                                    title="Quitar foto"
                                                    className="absolute -top-1.5 -right-1.5 bg-slate-800 text-white rounded-full w-5 h-5 flex items-center justify-center shadow-md"
                                                >
                                                    <X size={12} />
                                                </button>
                                            </div>
                                        ))}

                                        {/* ➕ Agregar nuevas fotos */}
                                        <label
                                            title="Agregar nuevas fotos"
                                            className="h-[72px] cursor-pointer flex flex-col items-center justify-center border-2 border-dashed border-slate-300 rounded-md text-blue-600 hover:bg-blue-50 hover:border-blue-400 transition"
                                        >
                                            <Camera size={18} />
                                            <span className="text-[9px] font-bold uppercase mt-0.5">Agregar</span>
                                            <input
                                                type="file"
                                                accept="image/*;capture=camera"
                                                multiple
                                                onChange={(e) => agregarImagenesNuevas(e.target.files)}
                                                className="hidden"
                                            />
                                        </label>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </section>
                </div>

                {/* Botonera de Acción (siempre visible) */}
                <div className="shrink-0 border-t border-slate-200 bg-slate-50 px-4 py-3 flex flex-col-reverse md:flex-row md:items-center justify-between gap-3">
                    <button
                        type="button"
                        onClick={() => navigate("/layout/ver-servicios")}
                        className="w-full md:w-auto flex justify-center items-center gap-2 h-10 px-4 rounded-lg text-[13px] font-bold text-slate-600 bg-white border border-slate-200 hover:bg-slate-100 transition"
                    >
                        <ArrowLeft size={16} /> Volver al historial
                    </button>
                    <button
                        type="submit"
                        disabled={loading || procesandoFotos > 0}
                        className="w-full md:w-56 flex justify-center items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white h-10 rounded-lg text-[13px] font-bold shadow-md shadow-blue-200 transition active:scale-[0.98] disabled:opacity-70"
                    >
                        {loading ? <Loader2 className="animate-spin" size={18} /> : procesandoFotos > 0 ? <><Loader2 className="animate-spin" size={18} /> Procesando fotos...</> : "GUARDAR CAMBIOS"}
                    </button>
                </div>

            </form>

            {/* Modal Zoom (Se mantiene igual) */}
            {imagenModal && (
                <div className="fixed inset-0 bg-black/80 z-[100] flex flex-col items-center justify-center p-4">
                    <div className="flex gap-4 mb-4 bg-white p-2 rounded-xl shadow-lg">
                        <button onClick={() => setRotacion(r => r + 90)}><RotateCw size={20} className="text-blue-600" /></button>
                        <button onClick={() => setZoom(z => Math.min(z + 0.2, 3))}><ZoomIn size={20} /></button>
                        <button onClick={() => setZoom(z => Math.max(z - 0.2, 0.5))}><ZoomOut size={20} /></button>
                        <button onClick={() => setImagenModal(null)} className="text-red-600"><X size={20} /></button>
                    </div>
                    <div className="relative flex items-center justify-center">
                        {/* Cargando... mientras llega la foto */}
                        {imagenModal !== imagenCargada && (
                            <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 text-white pointer-events-none">
                                <div className="w-10 h-10 border-4 border-white/30 border-t-white rounded-full animate-spin" />
                                <span className="text-xs font-bold uppercase tracking-widest">Cargando imagen...</span>
                            </div>
                        )}
                        <img
                            key={imagenModal}
                            src={imagenModal}
                            alt="Remito"
                            onLoad={() => setImagenCargada(imagenModal)}
                            onError={() => setImagenCargada(imagenModal)}
                            style={{ transform: `scale(${zoom}) rotate(${rotacion}deg)`, transformOrigin: "center center" }}
                            className={`max-h-[80vh] min-h-[200px] rounded-lg transition-[transform,opacity] duration-200 ${imagenModal !== imagenCargada ? "opacity-0" : "opacity-100"}`}
                        />
                    </div>
                </div>
            )}
        </div>
    );
}
